import { Container, Typography } from '@mui/material';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { getCookies } from 'cookies-next';
import { getName } from 'i18n-iso-countries';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { isValidCountry } from 'utils/country.utils';

import {
  DEFAULT_SENDER_COUNTRY,
  UNKNOWN_RECEIVER_COUNTRY,
} from 'constants/countries.constants';
import {
  COOKIES_KEY_RECEIVER_COUNTRY,
  COOKIES_KEY_SENDER_COUNTRY,
} from 'constants/storage.constants';
import { useLocale } from 'hooks/useLocale';
import {
  prefetchPromotions,
  usePromotions,
} from 'queries/promotions/usePromotions';
import { PromotionsParams } from 'types/promotion.types';

import SEO from 'components/@head/SEO';
import Layout from 'components/@prismic/Layout';
import PromotionsList from 'components/@promotions/PromotionsList';

interface Props {
  receiverCountry: string;
  senderCountry: string;
}

const Promotions = ({ receiverCountry, senderCountry }: Props) => {
  const { asPath } = useRouter();

  const asPathArr = asPath.split('/').filter((item) => !!item.length);

  const clientReceiverCountry = isValidCountry(asPathArr[0])
    ? asPathArr[0]
    : DEFAULT_SENDER_COUNTRY.toLowerCase();
  const clientSenderCountry = isValidCountry(asPathArr[1])
    ? asPathArr[1]
    : DEFAULT_SENDER_COUNTRY.toLowerCase();

  const { data, isLoading } = usePromotions({
    receiverCountry: receiverCountry ?? clientReceiverCountry,
    senderCountry: senderCountry ?? clientSenderCountry,
  });

  const locale = useLocale();
  const intl = useIntl();

  const handleTrackViewPromotionOverview = useCallback(async () => {
    if (data) {
      const { waitForAvo } = await import('services/avo');
      const { viewPromotionOverview } = await import('services/avo/avo');

      await waitForAvo();
      viewPromotionOverview();
    }
  }, [data]);

  useEffect(() => {
    handleTrackViewPromotionOverview();
  }, [handleTrackViewPromotionOverview]);

  return (
    <>
      <SEO
        title={intl.formatMessage(
          {
            id: 'promotions.seo.title',
          },
          {
            country: getName(receiverCountry ?? clientReceiverCountry, locale),
          },
        )}
      />
      <Layout>
        <Container maxWidth="sm">
          <Typography
            variant="h1"
            color="brand.red"
            mt={{ xs: 12, md: 25 }}
            mb={6}
          >
            <FormattedMessage id="label.promotions" />
          </Typography>
          <Typography variant="h4" mb={8}>
            <FormattedMessage id="page.promotions.description" />
          </Typography>
          <PromotionsList promotions={data} isLoading={isLoading} />
        </Container>
      </Layout>
    </>
  );
};

export const getServerSideProps = async ({
  params,
  locale,
  req,
  res,
}: GetServerSidePropsContext<PromotionsParams>) => {
  try {
    const queryClient = new QueryClient();
    const cookies = getCookies({ req, res });

    const receiverCountry =
      (cookies[COOKIES_KEY_RECEIVER_COUNTRY] as string) ||
      (params?.receiverCountry as string);
    const senderCountry =
      (cookies[COOKIES_KEY_SENDER_COUNTRY] as string) ||
      (params?.senderCountry as string);

    await prefetchPromotions(
      queryClient,
      {
        receiverCountry:
          receiverCountry === UNKNOWN_RECEIVER_COUNTRY
            ? DEFAULT_SENDER_COUNTRY.toLowerCase()
            : receiverCountry,
        senderCountry,
      },
      locale,
    );

    return {
      props: {
        receiverCountry,
        senderCountry,
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (error) {
    console.error(error);
  }
};

export default Promotions;
